/**
 * Localizes the two edge-function AAL2-gate refusal codes
 * (`supabase/functions/_shared/requireAal2.ts` on the server —
 * `MFA_REQUIRED` / `MFA_CHECK_UNAVAILABLE`) wherever a client turns an
 * edge-function error body into user-facing text.
 *
 * Several of the functions that call `requireAal2` / `requireAal2IfEnrolled`
 * (the user-delete, turn-off-2FA, invite and invite-management functions) return
 * ONLY `{ error: <raw English message> }` — no `code` field reaches the
 * client at all — so this ALSO matches on the two gate messages' exact,
 * stable English text (a single shared constant on the server) as a
 * fallback for those surfaces. Other edge functions (member activate/
 * deactivate, role change, every platform function) route the same gate through
 * a shared `ApiError` and DO return `{ error: { code, message } }`, so a
 * `.code` (or a bare code string) is matched first and preferred.
 *
 * Unknown codes/messages return `null` from both exports — every caller's
 * EXISTING fallback logic runs unchanged, so this can never suppress or
 * alter any error this app already knows how to show.
 */

const MFA_REQUIRED_SERVER_MESSAGE =
  'Multi-factor authentication must be completed for this action. Sign out and sign back in with your authenticator, then try again.';
const MFA_CHECK_UNAVAILABLE_SERVER_MESSAGE =
  "We couldn't verify your sign-in security right now. Please try again in a moment.";

/**
 * @param {unknown} error a code string, an `{ code, message? }`-shaped
 *   object/Error, or a raw message string.
 * @returns {'MFA_REQUIRED'|'MFA_CHECK_UNAVAILABLE'|null}
 */
export function getEdgeMfaGateCode(error) {
  if (!error) return null;
  const code = typeof error === 'string' ? error : (typeof error.code === 'string' ? error.code : null);
  if (code === 'MFA_REQUIRED' || code === 'MFA_CHECK_UNAVAILABLE') return code;
  const message = typeof error === 'string' ? error : (typeof error.message === 'string' ? error.message : null);
  if (message === MFA_REQUIRED_SERVER_MESSAGE) return 'MFA_REQUIRED';
  if (message === MFA_CHECK_UNAVAILABLE_SERVER_MESSAGE) return 'MFA_CHECK_UNAVAILABLE';
  return null;
}

/**
 * @param {unknown} error same shapes as `getEdgeMfaGateCode`.
 * @param {object} intl react-intl instance.
 * @returns {string|null} localized copy, or `null` when `error` isn't one of
 *   the two gate refusals — callers fall back to their own existing mapping.
 */
export function getEdgeMfaGateErrorMessage(error, intl) {
  const code = getEdgeMfaGateCode(error);
  if (code === 'MFA_REQUIRED') {
    return intl.formatMessage({
      id: 'errors.edgeMfaGate.required',
      defaultMessage: 'Finish two-step verification for this action: sign out, sign back in with your authenticator, then try again.',
    });
  }
  if (code === 'MFA_CHECK_UNAVAILABLE') {
    return intl.formatMessage({
      id: 'errors.edgeMfaGate.checkUnavailable',
      defaultMessage: "We couldn't verify your sign-in security right now. Please try again in a moment.",
    });
  }
  return null;
}
